import type { Stop, LineDetails } from "../types";
import { getActiveTpid } from "./timetablePeriods";
import { API_ENDPOINTS } from "./apiEndpoints";
import { isAbortError } from "../utils/errors";

// Helpers to pick icon/group CSS from various possible locations
function pickIconCss(src: any): string | undefined {
  return (
    src?.LineType?.OgtTrafficTypeIconCssClass ??
    src?.Ogt?.OgtTrafficTypeIconCssClass ??
    src?.OgtTrafficTypeIconCssClass ??
    src?.LineTypes?.[0]?.OgtTrafficTypeIconCssClass
  );
}

function pickGroupCss(src: any): string | undefined {
  return (
    src?.LineType?.OgtTrafficTypeGroupCssClass ??
    src?.Ogt?.OgtTrafficTypeGroupCssClass ??
    src?.OgtTrafficTypeGroupCssClass ??
    src?.LineTypes?.[0]?.OgtTrafficTypeGroupCssClass
  );
}

// inflight abort controller for find
let findAbortController: AbortController | null = null;

const detailsInFlight = new Map<string, Promise<LineDetails>>();
const detailsCache = new Map<string, LineDetails>();

function uniqueBy<T>(arr: T[], getKey: (x: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const it of arr) {
    const k = getKey(it);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(it);
    }
  }
  return out;
}

// Fetch wrapper with timeout and abort signal combination
function fetchWithTimeout(
  input: RequestInfo,
  init: RequestInit = {},
  timeoutMs = 12000
) {
  const timeoutCtrl = new AbortController();
  const timeoutId = setTimeout(
    () => timeoutCtrl.abort(new DOMException("Timeout", "AbortError")),
    timeoutMs
  );
  const signal = init.signal
    ? (AbortSignal as any).any
      ? (AbortSignal as any).any([init.signal, timeoutCtrl.signal])
      : combineSignals(init.signal, timeoutCtrl.signal)
    : timeoutCtrl.signal;

  return fetch(input, { ...init, signal }).finally(() =>
    clearTimeout(timeoutId)
  );
}

function combineSignals(a: AbortSignal, b: AbortSignal) {
  const ctrl = new AbortController();
  const onAbort = (s: AbortSignal) => s.aborted && ctrl.abort(s.reason);
  a.addEventListener("abort", () => ctrl.abort(a.reason), { once: true });
  b.addEventListener("abort", () => ctrl.abort(b.reason), { once: true });
  onAbort(a);
  onAbort(b);
  return ctrl.signal;
}

function pickMunicipalityNames(src: any): string[] {
  const out: string[] = [];

  if (Array.isArray(src?.Municipalities)) {
    for (const m of src.Municipalities) {
      const name = String(m?.MunicipalityName ?? "").trim();
      if (name) out.push(name);
    }
  }

  const periods = src?.LinePeriods;
  if (periods && typeof periods === "object") {
    for (const p of Object.values(periods)) {
      const ms = (p as any)?.Municipalities;
      if (Array.isArray(ms)) {
        for (const m of ms) {
          const name = String(m?.MunicipalityName ?? "").trim();
          if (name) out.push(name);
        }
      }
    }
  }

  const ogtMs = src?.Ogt?.OgtMunicipalities;
  if (Array.isArray(ogtMs)) {
    for (const m of ogtMs) {
      const name = String(m?.MunicipalityName ?? "").trim();
      if (name) out.push(name);
    }
  }

  return Array.from(new Set(out));
}

// WFS request XML builder
function buildWfsRequestXml(lineId: string, tpid: string): string {
  const xml = `
    <GetFeature service="WFS" version="1.0.0" outputFormat="application/json"
      xmlns:wfs="http://www.opengis.net/wfs"
      xmlns:ogc="http://www.opengis.net/ogc"
      xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
      xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-basic.xsd">
      <wfs:Query typeName="ogt:linjer3">
        <wfs:PropertyName>hplllista</wfs:PropertyName>
        <wfs:PropertyName>linje</wfs:PropertyName>
        <wfs:PropertyName>geom</wfs:PropertyName>
        <wfs:PropertyName>trafiktyp</wfs:PropertyName>
        <ogc:Filter>
          <ogc:And>
            <ogc:PropertyIsEqualTo>
              <ogc:PropertyName>tpid</ogc:PropertyName>
              <ogc:Literal>${tpid}</ogc:Literal>
            </ogc:PropertyIsEqualTo>
            <ogc:PropertyIsEqualTo>
              <ogc:PropertyName>linje</ogc:PropertyName>
              <ogc:Literal>${lineId}</ogc:Literal>
            </ogc:PropertyIsEqualTo>
          </ogc:And>
        </ogc:Filter>
      </wfs:Query>
    </GetFeature>
  `.trim();
  return `=${encodeURIComponent(xml)}`;
}

// API: Lines search/autocomplete
export async function fetchLines(query: string): Promise<LineDetails[]> {
  if (findAbortController) findAbortController.abort();
  findAbortController = new AbortController();

  try {
    const url = API_ENDPOINTS.LINES_SEARCH(query, true);

    const res = await fetchWithTimeout(
      url,
      { signal: findAbortController.signal },
      10000
    );

    if (!res.ok) throw new Error(`Lines API error: ${res.status}`);

    const arr: any[] = await res.json();

    const mapped: LineDetails[] = arr.map((item: any) => {
      const iconCss = pickIconCss(item);
      const groupCss = pickGroupCss(item);

      const municipalityNames = pickMunicipalityNames(item);
      const municipalityName = municipalityNames[0];

      return {
        id: String(item.LineNrReal),
        name: `Linje ${item.LineName ?? item.LineNrReal} ${
          item.Heading ?? ""
        }`.trim(),
        LineName: String(item.LineName ?? item.LineNrReal ?? ""),
        LineNrReal: String(item.LineNrReal),
        Heading: String(item.Heading ?? ""),
        start: item.Ll
          ? { lat: item.Ll[0], lng: item.Ll[1] }
          : { lat: 0, lng: 0 },
        end: item.Ll
          ? { lat: item.Ll[0], lng: item.Ll[1] }
          : { lat: 0, lng: 0 },
        stops: [],
        paths: [[]],
        iconCss,
        groupCss,
        municipalityNames,
        municipalityName,
      };
    });

    return uniqueBy(mapped, (x) => `${x.LineNrReal}__${x.Heading || ""}`);
  } catch (err) {
    if (isAbortError(err)) return [];
    throw err;
  }
}

// API: Lines/Details
export async function fetchLineDetails(id: string): Promise<LineDetails> {
  const tpidNum = await getActiveTpid();
  if (tpidNum == null) throw new Error("Ingen aktiv tidtabellsperiod hittades");
  const tpid = String(tpidNum);
  const cacheKey = `${id}__TP${tpid}`;

  const cached = detailsCache.get(cacheKey);
  if (cached) return cached;

  const inflight = detailsInFlight.get(cacheKey);
  if (inflight) return inflight;

  const p = (async () => {
    const tpidNum = await getActiveTpid();
    if (tpidNum == null)
      throw new Error("Ingen aktiv tidtabellsperiod hittades");
    const tpid = String(tpidNum);
    const bodyPayload = buildWfsRequestXml(id, tpid);

    const detailsReq = (async () => {
      const maxAttempts = 2;
      let lastErr: unknown;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const res = await fetchWithTimeout(
            API_ENDPOINTS.LINES_DETAILS(id),
            {},
            10000
          );
          if (!res.ok)
            throw new Error(`API (LinesDetails) error: ${res.status}`);
          const json = await res.json();
          if (!Array.isArray(json) || json.length === 0) {
            throw new Error(`Inga detaljer hittades för linje ${id}`);
          }
          return json[0];
        } catch (e) {
          lastErr = e;
          if (attempt < maxAttempts) {
            await new Promise((r) => setTimeout(r, 350 + Math.random() * 250));
          }
        }
      }
      throw lastErr;
    })();

    const geoReq = (async () => {
      const geoRes = await fetchWithTimeout(
        API_ENDPOINTS.GEO_FETCH,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: bodyPayload,
        },
        12000
      );
      if (!geoRes.ok) {
        const text = await geoRes.text().catch(() => "");
        throw new Error(
          `GeoAPI error: ${geoRes.status}${text ? ` - ${text}` : ""}`
        );
      }
      return geoRes.json();
    })();

    const [line, geoData] = await Promise.all([detailsReq, geoReq]);

    function coordsToPath(coords: [number, number][]) {
      return coords.map(([lng, lat]) => ({ lat, lng }));
    }

    const paths: { lat: number; lng: number }[][] = (
      geoData.features || []
    ).flatMap((f: any) => {
      const geom = f.geometry;
      if (!geom) return [];

      if (geom.type === "LineString") {
        return [coordsToPath(geom.coordinates)];
      }

      if (geom.type === "MultiLineString") {
        return (geom.coordinates as [number, number][][]).map((segment) =>
          coordsToPath(segment)
        );
      }

      return [];
    });

    const pathArray = paths.length ? paths : [[]];

    const stops: Stop[] = (line.PassingStopsWithStopPoints || []).flatMap(
      (stop: any) => {
        const [parentLat, parentLng] = stop.Ll;
        return (stop.OgtStopPointDatas || []).map((sp: any) => ({
          id: `${stop.Id}-${sp.stopPointId}`,
          name: sp.stopPointAlias
            ? `${stop.PlaceName} (${sp.stopPointAlias})`
            : stop.PlaceName,
          lat: sp.ll[0],
          lng: sp.ll[1],
          type: sp.stopPointAlias,
          parentLat,
          parentLng,
        }));
      }
    );

    const start = stops.length
      ? { lat: stops[0].lat, lng: stops[0].lng }
      : { lat: 0, lng: 0 };
    const end = stops.length
      ? { lat: stops[stops.length - 1].lat, lng: stops[stops.length - 1].lng }
      : { lat: 0, lng: 0 };

    const detailId = line.LineNrReal?.toString() ?? id;

    // meta for icon/color
    const iconCss = pickIconCss(line);
    const groupCss = pickGroupCss(line);

    // municipality
    const municipalityNames = pickMunicipalityNames(line);
    const municipalityName = municipalityNames[0];

    const details: LineDetails = {
      id: detailId,
      name: `Linje ${line.LineName ?? detailId} ${line.Heading ?? ""}`.trim(),
      LineName: line.LineName?.toString() ?? "",
      LineNrReal: detailId,
      LineNameAsNumberOrNull: line.LineNameAsNumberOrNull?.toString() ?? "",
      NotVeryNiceLookingTimeTableName:
        line.NotVeryNiceLookingTimeTableName?.toString() ?? "",
      Heading: line.Heading ?? "",
      start,
      end,
      stops,
      paths: pathArray,
      // meta for icon/color
      iconCss,
      groupCss,
      // municipality
      municipalityNames,
      municipalityName,
    };

    detailsCache.set(cacheKey, details);
    return details;
  })().finally(() => {
    detailsInFlight.delete(cacheKey);
  });

  detailsInFlight.set(cacheKey, p);
  return p;
}
